<template>
  <div class="edit-transaction-screen">
    <h2>Who / Sponsor / Member</h2>
    <time>
      {{ timestamp }}
    </time>
    <div class="transaction-member-list">
      <div
        v-for="entity in entities"
        :key="entity.id"
        class="transaction-entity"
      >
        <div
          class="transaction-member"
          v-for="member in entity.people"
          :key="member.id"
          :class="{ error: !isValid('sponsors') || !isValid('members') }"
        >
          <span class="transaction-member__name">
            <Person
              :size="AVATAR_SIZES.SMALL"
              v-bind="member"
            />
            {{ member.name }}
          </span>
          <input
            class="amount"
            type="number"
            step="1"
            min="0"
            placeholder="paid"
            v-model.number="sponsors[member.id]"
          >
          <input type="checkbox" :value="member.id" v-model="transactionMembers">
        </div>
      </div>
    </div>
    <div class="input-box">
      <div>
        <input
          type="text"
          class="purpose"
          placeholder="what"
          maxlength="26"
          autofocus
          v-model="purpose"
          :class="{ error: !isValid('purpose') }"
          @keydown.enter="trySave"
        />
      </div>
    </div>
    <button type="button" @click="trySave">Save</button>
    <button v-if="transactionId" class="delete" type="button" @click="remove">Remove</button>
    <router-link :to="`/${gigId}`" class="back">back</router-link>
  </div>
</template>

<script>
import { db, FieldValue } from '../db.js';
import { AVATAR_SIZES } from '../constants.js';
import Person from './Person.vue';

export default {
  name: 'EditTransactionScreen',
  components: {
    Person,
  },
  props: {
    gigId: String,
    transactionId: String,
  },
  data() {
    return {
      AVATAR_SIZES,
      sponsors: {},
      entities: [],
      people: [],
      transactionMembers: [],
      purpose: '',
      formErrors: [],
      timestamp: null,
    };
  },
  methods: {
    trySave() {
      this.validate();
      if (this.isValid()) {
        this.save();
      }
    },
    save() {
      const {
        $router,
        gigId,
        purpose,
        sponsors,
        transactionId,
        transactionMembers,
      } = this;
      const gig = db.collection('gigs').doc(gigId);
      const sponsorEntries = Object.entries(sponsors)
        .filter(([, amount]) => amount > 0)
        .map(([id, amount]) => ({
          person: this.$firestoreRefs.people.doc(id),
          amount,
        }));
      const data = {
        purpose,
        sponsors: sponsorEntries,
        members: transactionMembers.map(id => this.$firestoreRefs.people.doc(id)),
      };
      if (transactionId) {
        gig.collection('transactions').doc(transactionId).update(data);
      } else {
        gig.collection('transactions').add({
          ...data,
          timestamp: FieldValue.serverTimestamp(),
        });
      }
      $router.push('/' + gigId);
    },
    validate() {
      this.formErrors = [];
      if (!this.purpose || this.purpose.length === 0) {
        this.formErrors.push({ purpose: 'Purpose cannot be empty' });
      }
      if (!Object.values(this.sponsors).filter(value => value > 0)) {
        this.formErrors.push({ sponsors: 'Someone must have paid for it' });
      }
      if (!this.transactionMembers.length) {
        this.formErrors.push({ members: 'Someone must have been paid for' });
      }
    },
    isValid(key) {
      if (key) {
        return this.formErrors.filter(error => key in error).length === 0;
      } else {
        return this.formErrors.length === 0;
      }
    },
    remove() {
      if (!confirm('Are you sure?')) {
        return;
      }
      db.collection('gigs').doc(this.gigId)
        .collection('transactions').doc(this.transactionId)
        .delete();
      this.back();
    },
    back() {
      this.$router.back();
    },
  },
  beforeMount() {
    const {
      gigId,
      transactionId,
    } = this;
    const gig = db.collection('gigs').doc(gigId);
    if (transactionId) {
      const transaction = gig.collection('transactions').doc(transactionId);
      transaction.get().then(doc => {
        this.timestamp = new Intl.DateTimeFormat(undefined, {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }).format(doc.data().timestamp.toDate());
        this.purpose = doc.data().purpose;
        const sponsorEntries = doc.data().sponsors;
        this.sponsors = sponsorEntries.reduce((acc, {person, amount}) => {
          acc[person.id] = amount;
          return acc;
        }, {});
        this.transactionMembers = doc.data().members.map(ref => ref.id);
      });
    }
  },
  watch: {
    gigId: {
      immediate: true,
      handler(gigId) {
        const gig = db.collection('gigs').doc(gigId);
        this.$bind('people', gig.collection('people'));
        this.$bind('entities', gig.collection('entities')).then(entities => {
          if (this.transactionId) {
            return;
          }
          entities.forEach(entity => {
            this.transactionMembers.push(...entity.people.map(ref => ref.id));
          });
        });
      },
    },
  },
}
</script>

<style scoped>
.edit-transaction-screen {
  padding-top: 100px;
  text-align: center;
}
h1 {
  margin-top: 0;
  color: #6d547e;
}
.transaction-entity {
  border: 1px solid lightgrey;
  border-radius: 1em;
  background: #fff;
  margin: .5em 0;
  padding: 0 1em;
}
.transaction-member.error {
  color: red;
}
input {
  margin: 10px;
  padding: 10px;
  border-radius: 10px;
  outline: none;
  font-size: 24px;
  border: 1px solid #6d547e;
}
input.error {
  border: 1px solid red;
}
input.purpose {
  width: 200px;
}
input.amount {
  width: 150px;
}
button {
  margin: 10px;
  padding: 10px 15px;
  border-radius: 8px;
  border: none;
  background-color: #6d547e;
  font-size: 18px;
  color: white;
    outline: none;
}
button.delete {
  background-color: #f00;
}
h2 {
  text-align: center;
  color: #6d547e;
}
.transaction-member-list {
  margin: 0 auto;
  width: fit-content;
}
.transaction-member {
  display: flex;
  align-items: center;
}
.transaction-member__name {
  flex-grow: 1;
  text-align: left;
}
.back {
  display: inline-block;
  margin: 10px;
}
</style>
